'use client';

import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Venue } from '../../../../shared/models';
import { getOptimizedImageUrl } from '../../../utils/imageUtils';
import useEmblaCarousel from 'embla-carousel-react';

interface TrendingVenuesProps {
    venues: Venue[];
}

const TrendingVenues: React.FC<TrendingVenuesProps> = ({ venues }) => {
    // Track if the user is interacting with the carousel
    const userInteracting = useRef(false);
    const autoScrollInterval = useRef<NodeJS.Timeout | null>(null);

    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        loop: true,
        dragFree: true,
        slidesToScroll: 1,
        skipSnaps: false,
        containScroll: 'trimSnaps',
        duration: 500 // Controls the scroll animation duration in milliseconds (default is 25)
    });

    // Generate a price display string based on venue attributes
    const getPriceString = (venue: Venue): string => {
        const firstSectionKey = Object.keys(venue.PricingRatesPerSection)[0];
        const firstSection = venue.PricingRatesPerSection[firstSectionKey];

        if (firstSection && firstSection.RatePerHour > 0) {
            return `From $${firstSection.RatePerHour} min. spend`;
        }
        return 'View Pricing';
    };

    // Get venue photo or fallback to placeholder
    const getVenuePhoto = (venue: Venue): string => {
        if (venue.PhotoUrls && venue.PhotoUrls.length > 0) {
            return getOptimizedImageUrl(venue.PhotoUrls[0], 800, 85);
        }
        return getOptimizedImageUrl('/venues/placeholder.jpg', 800, 85);
    };

    // Get venue neighborhood
    const getNeighborhood = (venue: Venue): string => {
        if (venue.Neighborhoods && venue.Neighborhoods.length > 0) {
            return venue.Neighborhoods[0];
        }
        return venue.Address.split(',')[0];
    };

    // Function to start auto scrolling
    const startAutoScrolling = useCallback(() => {
        if (autoScrollInterval.current) {
            clearInterval(autoScrollInterval.current);
        }

        autoScrollInterval.current = setInterval(() => {
            if (!userInteracting.current && emblaApi && document.hasFocus()) {
                emblaApi.scrollNext();
            }
        }, 4500); // Increased from 2000ms to 3000ms for slower scrolling
    }, [emblaApi]);

    // Setup and clean up auto scrolling
    useEffect(() => {
        if (!emblaApi) return;

        // Set up event listeners for user interaction
        const onPointerDown = () => {
            userInteracting.current = true;
        };

        const onPointerUp = () => {
            userInteracting.current = false;
            // Restart auto-scrolling after short delay
            setTimeout(startAutoScrolling, 500);
        };

        // Listen for interaction events
        emblaApi.on('pointerDown', onPointerDown);
        emblaApi.on('pointerUp', onPointerUp);

        // Start auto-scrolling immediately
        startAutoScrolling();

        // Set up visibility change detection to pause when tab is inactive
        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (autoScrollInterval.current) {
                    clearInterval(autoScrollInterval.current);
                }
            } else {
                startAutoScrolling();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Clean up
        return () => {
            if (autoScrollInterval.current) {
                clearInterval(autoScrollInterval.current);
            }

            if (emblaApi) {
                emblaApi.off('pointerDown', onPointerDown);
                emblaApi.off('pointerUp', onPointerUp);
            }

            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [emblaApi, startAutoScrolling]);

    return (
        <section className="py-12 bg-[#1C1C34] font-['Barlow']" aria-labelledby="trending-venues-heading">
            <div className="container mx-auto px-4 md:px-6 mb-1">
                <h2 id="trending-venues-heading" className="text-l text-white font-['Barlow']">TRENDING VENUES</h2>
            </div>

            <div className="overflow-hidden px-4 relative" ref={emblaRef}>
                <div className="flex">
                    {venues.map((venue) => (
                        <article
                            key={venue.ID}
                            className="flex-none w-72 md:w-72 mx-2"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
                                <Link href={`/venue/${venue.ID}`} aria-label={`View details for ${venue.Name}`}>
                                    <Image
                                        src={getVenuePhoto(venue)}
                                        alt={`${venue.Name} - ${getNeighborhood(venue)}`}
                                        fill
                                        sizes="(max-width: 768px) 100vw, 300px"
                                        className="object-cover"
                                        priority={venues.indexOf(venue) <= 2}
                                    />
                                </Link>
                            </div>
                            <div className="pt-3 pb-6">
                                <h3 className="text-base text-white leading-tight mb-1 font-['Barlow']">
                                    <Link href={`/venue/${venue.ID}`} className="hover:text-blue-400 transition-colors">
                                        {venue.Name}{venue.Subtitle ? `: ${venue.Subtitle}` : ''}
                                    </Link>
                                </h3>
                                <p className="text-sm text-gray-400 mb-1">{getNeighborhood(venue)}</p>
                                <p className="text-sm text-gray-300">{getPriceString(venue)}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </div>

            <div className="container mx-auto px-4 md:px-6 mt-8 flex flex-col sm:flex-row items-center justify-between max-w-3xl mx-auto gap-4">
                <p className="text-white text-base font-['Barlow'] w-full text-left sm:mr-4">Your search for the perfect event venue in NYC ends here. Explore hidden gems and iconic hotspots for unforgettable celebrations.</p>
                <Link
                    href="/browse"
                    className="bg-[#C0C0DD] hover:bg-[#AAAAC8] text-black px-6 py-2 rounded-full text-base font-medium inline-block font-['Barlow'] whitespace-nowrap"
                    aria-label="Browse all venue listings in New York City"
                >
                    BROWSE VENUES
                </Link>
            </div>
        </section>
    );
};

export default TrendingVenues; 